import { Fragment, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useQuery } from '@tanstack/react-query';
import { AuditRecordSchema, type AuditRecordDto } from '@taqon/contracts';
import { Link as RouterLink } from 'react-router';
import { getPage } from '../api/client';
import { recordPath, viewCapability } from '../records/routes';
import { hasCapability, useSession } from '../session/session';
import { EmptyState, ErrorState, LoadingState, Section } from '../ui/page';
import { PersonName } from '../ui/person';

export const AUDIT_RECORDS = '/api/v1/platform/audit-records';

const EVENT_LABELS: Readonly<Record<string, string>> = {
  create: 'Created',
  change: 'Changed',
  status_change: 'Status changed',
  approval: 'Approval',
  posting: 'Posted',
  access: 'Accessed',
  unmasked_access: 'Viewed unmasked',
  export: 'Exported',
  custody_change: 'Custody changed',
  scan: 'Scanned',
  config_change: 'Configuration changed',
  sod_block: 'Blocked by segregation of duties',
  auth_failure: 'Sign-in refused',
  privileged_action: 'Privileged action',
  ai_assistance_used: 'AI assistance used',
};

export function eventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType;
}

/**
 * How a record's page reads its own history: a field by its name on the page, and a recorded
 * value — a list code, say — by its label. The audit trail stores the values as they were (SDD
 * §13.4); this only changes how they read. Anything a function does not name shows as recorded,
 * and a masked value is never relabelled.
 */
export interface HistoryLabels {
  readonly field?: (field: string) => string | undefined;
  readonly value?: (field: string, value: string) => string | undefined;
}

function fieldText(labels: HistoryLabels | undefined, field: string): string {
  return labels?.field?.(field) ?? field;
}

function valueText(
  labels: HistoryLabels | undefined,
  field: string,
  value: string | null,
  masked: boolean,
): string {
  if (value === null) return '—';
  if (masked) return value;
  return labels?.value?.(field, value) ?? value;
}

/**
 * A status change records `{ field, from, to }` in its context: those read like the field changes.
 */
function contextText(
  labels: HistoryLabels | undefined,
  context: Readonly<Record<string, unknown>>,
  key: string,
  value: unknown,
): string {
  const field = typeof context.field === 'string' ? context.field : undefined;
  if (labels !== undefined && field !== undefined && typeof value === 'string') {
    if (key === 'field') return fieldText(labels, value);
    if (key === 'from' || key === 'to') return valueText(labels, field, value, false);
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/**
 * The audit rows as a table whose rows expand to their field changes and context (FRD-CORE-030,
 * CORE-033). Masked values arrive masked from the API (SDD §13.4) and are marked as such.
 */
export function AuditTable({
  records,
  showSubject,
  label,
  labels,
}: {
  records: readonly AuditRecordDto[];
  showSubject: boolean;
  label: string;
  labels?: HistoryLabels | undefined;
}) {
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 40 }} />
          <TableCell>When</TableCell>
          <TableCell>Event</TableCell>
          <TableCell>By</TableCell>
          {showSubject && <TableCell>Record</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((record) => (
          <AuditRow key={record.id} record={record} showSubject={showSubject} labels={labels} />
        ))}
      </TableBody>
    </Table>
  );
}

function AuditRow({
  record,
  showSubject,
  labels,
}: {
  record: AuditRecordDto;
  showSubject: boolean;
  labels: HistoryLabels | undefined;
}) {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const details =
    record.fieldChanges.length > 0 || record.context !== null || record.correlationId !== null;
  const path =
    record.subjectRecordType !== null && record.subjectRecordId !== null
      ? recordPath(record.subjectRecordType, record.subjectRecordId)
      : null;
  const canOpen =
    path !== null &&
    record.subjectRecordType !== null &&
    hasCapability(session.data, viewCapability(record.subjectRecordType));
  const subjectText =
    record.subjectRecordType === null
      ? '—'
      : `${record.subjectRecordTypeName ?? record.subjectRecordType}${
          record.subjectLabel !== null ? ` — ${record.subjectLabel}` : ''
        }`;
  return (
    <Fragment>
      <TableRow hover>
        <TableCell>
          {details && (
            <IconButton
              size="small"
              aria-label={open ? 'Hide details' : 'Show details'}
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {new Date(record.occurredAt).toLocaleString()}
        </TableCell>
        <TableCell>
          <Chip size="small" variant="outlined" label={eventLabel(record.eventType)} />
        </TableCell>
        <TableCell>
          {record.actorName !== null ? (
            <PersonName name={record.actorName} userId={record.actorUserId} />
          ) : (
            (record.actorProcess ?? '—')
          )}
        </TableCell>
        {showSubject && (
          <TableCell sx={{ minWidth: 200 }}>
            {canOpen ? (
              <Link component={RouterLink} to={path} sx={{ fontWeight: 600 }}>
                {subjectText}
              </Link>
            ) : (
              subjectText
            )}
          </TableCell>
        )}
      </TableRow>
      {details && (
        <TableRow>
          <TableCell
            colSpan={showSubject ? 5 : 4}
            sx={{ py: 0, borderBottom: open ? undefined : 0 }}
          >
            <Collapse in={open} unmountOnExit>
              <Box sx={{ py: 1.5 }}>
                {record.fieldChanges.length > 0 && (
                  <Table size="small" aria-label="Field changes">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Before</TableCell>
                        <TableCell>After</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {record.fieldChanges.map((change) => (
                        <TableRow key={change.field}>
                          <TableCell>{fieldText(labels, change.field)}</TableCell>
                          <TableCell sx={{ overflowWrap: 'anywhere' }}>
                            {valueText(labels, change.field, change.previousValue, change.masked)}
                            {change.masked && ' (masked)'}
                          </TableCell>
                          <TableCell sx={{ overflowWrap: 'anywhere' }}>
                            {valueText(labels, change.field, change.newValue, change.masked)}
                            {change.masked && ' (masked)'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {record.correlationId !== null && (
                  <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                    <strong>Correlation ID:</strong>{' '}
                    <Link
                      component={RouterLink}
                      to={`/admin/audit?correlation=${encodeURIComponent(record.correlationId)}`}
                    >
                      {record.correlationId}
                    </Link>{' '}
                    (every record of the same request)
                  </Typography>
                )}
                {record.context !== null && (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {Object.entries(record.context).map(([key, value]) => (
                      <Typography key={key} variant="body2">
                        <strong>{key}:</strong>{' '}
                        {contextText(labels, record.context ?? {}, key, value)}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}

/**
 * A record's own history on its page (FRD-CORE-030: created, changed, status, approvals, previous
 * and new values). Shown only to a session that may read the audit trail; the list pages through
 * the whole history, newest first.
 */
export function RecordHistory({
  recordType,
  recordId,
  labels,
}: {
  recordType: string;
  recordId: string;
  /** How the page names its fields and list values in the history (optional). */
  labels?: HistoryLabels | undefined;
}) {
  const session = useSession();
  const canRead = hasCapability(session.data, 'platform.audit_record:view');
  const [page, setPage] = useState(1);
  const history = useQuery({
    queryKey: ['platform', 'audit', recordType, recordId, page],
    enabled: canRead,
    queryFn: () =>
      getPage(
        `${AUDIT_RECORDS}?pageSize=20&page=${String(page)}&filter[subject_record_type]=${recordType}&filter[subject_record_id]=${recordId}`,
        AuditRecordSchema,
      ),
  });
  if (!canRead) return null;
  const total = history.data?.total ?? 0;
  return (
    <Section
      title="History"
      actions={
        total > 20 ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="caption">
              Page {page} of {Math.ceil(total / 20)}
            </Typography>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => {
                setPage(page - 1);
              }}
            >
              Newer
            </Button>
            <Button
              size="small"
              disabled={page * 20 >= total}
              onClick={() => {
                setPage(page + 1);
              }}
            >
              Older
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {history.isPending && <LoadingState label="Loading history" />}
      {history.isError && <ErrorState message={history.error.message} />}
      {history.data?.items.length === 0 && <EmptyState title="No recorded history" />}
      {history.data !== undefined && history.data.items.length > 0 && (
        <AuditTable
          records={history.data.items}
          showSubject={false}
          label="Record history"
          labels={labels}
        />
      )}
    </Section>
  );
}
